/**
 * BrandIcon — filled, brand-coloured marks for a person's contact channels.
 *
 * SCOPE: candidate contact rails only (see CandidateCards).
 *
 * They render in NEUTRAL ink, not brand colour. Four saturated hues in the
 * quietest corner of the card out-shouted the scores, which are the only thing
 * on it allowed to carry colour. The recognition survives because it was never
 * the hue doing the work — the official filled silhouette is what reads as
 * LinkedIn at 16px.
 *
 * They are filled, not outlined: at 16px an outlined glyph loses its silhouette,
 * and the silhouette is the entire signal. No background chip — the mark sits
 * bare on the card.
 *
 * DO NOT use these for UI actions. Every other icon in the app comes from the
 * `ia-*` icon font and takes its colour from tokens. Adding a fifth brand here
 * means adding a real, filled, official mark — not a generic glyph tinted to
 * look like one.
 */

export type BrandMark = 'linkedin' | 'github' | 'email' | 'phone';

/** Official marks, filled. Paths are single-colour so `fill: currentColor` works. */
const PATHS: Record<BrandMark, string> = {
  linkedin:
    'M6.94 5a2 2 0 1 1-4-.02 2 2 0 0 1 4 .02ZM3 8.5h3.9V21H3V8.5Zm6.3 0H13v1.7h.05c.5-.9 1.7-1.9 3.6-1.9 3.8 0 4.5 2.5 4.5 5.8V21h-3.9v-5.4c0-1.3 0-3-1.8-3s-2.1 1.4-2.1 2.9V21H9.3V8.5Z',
  github:
    'M12 .5C5.7.5.5 5.7.5 12a11.5 11.5 0 0 0 7.9 10.9c.6.1.8-.2.8-.6v-2c-3.2.7-3.9-1.5-3.9-1.5-.5-1.3-1.3-1.7-1.3-1.7-1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1 1.8 2.7 1.3 3.4 1 .1-.8.4-1.3.7-1.6-2.6-.3-5.3-1.3-5.3-5.7 0-1.3.5-2.3 1.2-3.1-.1-.3-.5-1.5.1-3.1 0 0 1-.3 3.3 1.2a11.4 11.4 0 0 1 6 0C18.6 4.8 19.6 5.1 19.6 5.1c.6 1.6.2 2.8.1 3.1.8.8 1.2 1.8 1.2 3.1 0 4.4-2.7 5.4-5.3 5.7.4.4.8 1.1.8 2.2v3.3c0 .4.2.7.8.6A11.5 11.5 0 0 0 23.5 12C23.5 5.7 18.3.5 12 .5Z',
  email:
    'M2 5.5A1.5 1.5 0 0 1 3.5 4h17A1.5 1.5 0 0 1 22 5.5v.2l-10 6-10-6v-.2Zm0 2.3V18a1.5 1.5 0 0 0 1.5 1.5h17A1.5 1.5 0 0 0 22 18V7.8l-9.5 5.7a1 1 0 0 1-1 0L2 7.8Z',
  phone:
    'M6.6 10.8a15.5 15.5 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.2c1.1.4 2.3.6 3.6.6a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.3.2 2.5.6 3.6a1 1 0 0 1-.2 1l-2.3 2.2Z',
};

/** The confirmation mark shown in place of the brand once a value is copied. */
export const CHECK_PATH = 'm5 13 4 4L19 7';

const BrandIcon = ({ mark, className }: { mark: BrandMark; className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden focusable="false">
    <path d={PATHS[mark]} />
  </svg>
);

export default BrandIcon;
